/**
 * refactorFlow · «Пошаговый рефактор: куча скриптов → аккуратно» (S15 · T11 капстоун).
 * Per ADR-014. Применяй ходы-рефакторы ПО ПОРЯДКУ — код в панели мутирует на каждом
 * шаге (готовые состояния в <template>, детерминированно, без парсера). Вредные ходы
 * (data-bad) отклоняются с объяснением, код не меняется. Все шаги → код аккуратный.
 *
 * Контракт разметки:
 *   <div class="rflow" data-fin="мораль финала">
 *     <div class="rf-head"><span class="rf-ttl">…</span>
 *       <span class="rf-prog">шаг <b class="rf-step">0</b>/N</span></div>
 *     <div class="rf-instr">…</div>
 *     <pre class="rf-code"><code>СТАРТ: грязный код</code></pre>
 *     <div class="rf-moves">
 *       <button type="button" class="rf-move" data-apply="fixture">…</button>   (нужные ходы по порядку)
 *       <button type="button" class="rf-move" data-bad="sleep" data-why="…">…</button> (соблазны)
 *     </div>
 *     <div class="rf-verdict" aria-live="polite"></div>
 *     <template class="rf-tpl" data-apply="fixture">КОД ПОСЛЕ ШАГА</template> …(в порядке шагов)
 *   </div>
 *
 * No-JS: виден стартовый код + список ходов. StrictMode-safe (guard + cleanup
 * восстанавливает innerHTML). См. feedback_qa_studio_strictmode_interactive_modules.
 */
export function initRefactorFlow(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.rflow').forEach((el) => {
    if (el.dataset.rfInit === '1') return;
    el.dataset.rfInit = '1';
    const original = el.innerHTML;

    const codeEl =
      el.querySelector<HTMLElement>('.rf-code code') ?? el.querySelector<HTMLElement>('.rf-code');
    const stepEl = el.querySelector<HTMLElement>('.rf-step');
    const verdict = el.querySelector<HTMLElement>('.rf-verdict');
    const moves = Array.from(el.querySelectorAll<HTMLButtonElement>('.rf-move'));
    const goodMoves = moves.filter((m) => m.dataset.apply);
    if (!codeEl || goodMoves.length === 0) return;

    el.classList.add('rf-ready');

    const tpl = new Map<string, string>();
    el.querySelectorAll<HTMLTemplateElement>('template.rf-tpl').forEach((t) => {
      const id = t.dataset.apply ?? '';
      const text = (t.content?.textContent ?? t.textContent ?? '').replace(/^\n/, '').replace(/\s+$/, '');
      tpl.set(id, text);
    });

    let step = 0;
    const setVerdict = (text: string, cls: '' | 'rf-err' | 'rf-ok') => {
      if (!verdict) return;
      verdict.textContent = text;
      verdict.className = 'rf-verdict' + (cls ? ' ' + cls : '');
    };

    const onMove = (e: Event) => {
      const btn = e.currentTarget as HTMLButtonElement;
      if (el.classList.contains('rf-done')) return;
      if (btn.dataset.bad) {
        btn.classList.add('rf-shake');
        window.setTimeout(() => btn.classList.remove('rf-shake'), 350);
        setVerdict(btn.dataset.why ?? 'Этот ход вредный — не применяй.', 'rf-err');
        return;
      }
      const expected = goodMoves[step];
      if (btn !== expected) {
        setVerdict('Сначала примени предыдущий шаг по порядку.', 'rf-err');
        return;
      }
      const code = tpl.get(btn.dataset.apply ?? '');
      if (code !== undefined) codeEl.textContent = code;
      btn.classList.add('rf-applied');
      btn.disabled = true;
      step += 1;
      if (stepEl) stepEl.textContent = String(step);
      if (step >= goodMoves.length) {
        el.classList.add('rf-done');
        moves.forEach((m) => {
          m.disabled = true;
        });
        setVerdict(
          el.dataset.fin ||
            'Код аккуратный: pytest-функция, Page Object, настоящая проверка — без лишней архитектуры.',
          'rf-ok',
        );
      } else {
        setVerdict('Готово. Следующий шаг.', '');
      }
    };

    moves.forEach((m) => m.addEventListener('click', onMove));

    cleanups.push(() => {
      moves.forEach((m) => m.removeEventListener('click', onMove));
      el.innerHTML = original;
      el.classList.remove('rf-ready', 'rf-done');
      delete el.dataset.rfInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
